import { ENTERPRISE_ROLE_PERMISSIONS, ROLE_RESOURCE_RESTRICTIONS } from "../data/roleRestrictions";
import {
    AMAZE_ACTION,
    AMAZE_RESOURCE,
    ENTERPRISE_RESOURCE,
    ENTERPRISE_ROLE_ID,
    ICurrentUser,
    IEnterpriseLoginRes,
    IUserPermission,
    ROLE,
} from "../types";

const DEFAULT_ROLE: ROLE = "user";
const VALID_ENTERPRISE_ROLE_IDS: ENTERPRISE_ROLE_ID[] = ["SUPER_USER", "ORG_ADMIN", "ORG_EDITOR", "ORG_READER"];
// 團隊網域預設視為 admin。這是「網域規則」而非「帳號清單」——不含任何真實使用者 email，
// 弱掃 P1 抓的是 bundle 內的 email allowlist（帳號洩漏），網域字串不屬此範疇，故可安全留前端。
// 內部 admin 帳號（原 auth.ts 的 ADMIN_EMAIL 5 筆）「不」再硬編於此：它們已收錄於 Worker
// 權限清單，會經由下方 usersRole 比對判成 admin，避免真實 email 明碼進 bundle 而使弱掃 P1 復發。
const TEAM_MEMBER_EMAIL_DOMAINS = ["barkingdog.ai"];

// 角色判定順序（清單優先，網域為 fallback）：
// 1. Worker 權限清單（usersRole）逐筆比對——清單中有此 email 時「一律以清單指定的 role 為準」，
//    即使是 barkingdog.ai 網域也不覆寫（例：honmyue@barkingdog.ai 清單設 user，就維持 user）。
// 2. 清單查無此 email，但屬團隊網域（barkingdog.ai）→ admin（前端網域規則，供未列於清單的內部同仁）。
// 3. 兩者皆非（或清單取用失敗為空且非團隊網域）→ 安全降級為 DEFAULT_ROLE（一般 user）。
export const getUserRole = (email?: string, usersRole?: IUserPermission[]) => {
    if (!email) return DEFAULT_ROLE;

    // 清單優先：清單明確指定 role 時，以清單為準，網域規則不得覆寫。
    const listedRole = usersRole?.find((user) => user.email === email)?.role;
    if (listedRole) return listedRole;

    // 清單查無此 email 時，才套用團隊網域規則升為 admin。
    const [, domain] = email.split("@");
    if (TEAM_MEMBER_EMAIL_DOMAINS.includes(domain)) return "admin";

    return DEFAULT_ROLE;
};

export const getEnterpriseLoginTokens = (data?: IEnterpriseLoginRes | null) => ({
    accessToken: data?.amaze_access_token || data?.access_token || "",
    refreshToken: data?.amaze_refresh_token || data?.refresh_token || "",
});

interface StoredEnterpriseUserParams {
    enterpriseAmazeAccountId: string | null;
    enterpriseAccountId: string | null;
    enterpriseRoles: string | null;
    enterpriseUserId: string | null;
    enterpriseIsFirstTimeLogin: string | null;
}

export const getStoredEnterpriseUser = ({
    enterpriseAmazeAccountId,
    enterpriseAccountId,
    enterpriseRoles,
    enterpriseUserId,
    enterpriseIsFirstTimeLogin,
}: StoredEnterpriseUserParams): ICurrentUser | null => {
    if (!enterpriseAccountId || !enterpriseRoles) return null;

    try {
        const parsedEnterpriseRoles = JSON.parse(enterpriseRoles) as ENTERPRISE_ROLE_ID[];
        if (
            !Array.isArray(parsedEnterpriseRoles) ||
            parsedEnterpriseRoles.some((role) => !VALID_ENTERPRISE_ROLE_IDS.includes(role))
        ) {
            return null;
        }

        return {
            accountId: enterpriseAmazeAccountId || enterpriseAccountId,
            bindLine: false,
            email: "",
            enterpriseAccountId,
            enterpriseRoles: parsedEnterpriseRoles,
            enterpriseUserId: enterpriseUserId || null,
            isFirstTimeLogin: enterpriseIsFirstTimeLogin === "true",
            role: "enterprise_user",
            username: "",
        };
    } catch {
        return null;
    }
};

interface PermissionCheckParams {
    role?: ROLE;
    resource: AMAZE_RESOURCE;
    action: AMAZE_ACTION;
}

export const checkHasPermission = ({ role, resource, action }: PermissionCheckParams) => {
    if (!role) {
        return false;
    }
    const isRestricted = ROLE_RESOURCE_RESTRICTIONS.find(
        (policy) => policy.roles.includes(role) && policy.resource === resource && policy.action === action
    );

    return !isRestricted;
};

export const checkEnterprisePermission = (
    enterpriseRoles: ENTERPRISE_ROLE_ID[] | null | undefined,
    resource: ENTERPRISE_RESOURCE,
    action: AMAZE_ACTION
) => {
    if (!enterpriseRoles) return false;
    return enterpriseRoles.some((r) => {
        const rolesPermissions = ENTERPRISE_ROLE_PERMISSIONS[r];
        return rolesPermissions[resource].includes(action);
    });
};
